import axios from 'axios';

interface StreamConversationData {
  success: boolean;
  data: {
    session_id: string;
    conversation_id: string;
    user_token: string;
  };
}

interface StreamConversationResult {
  user: string;
  token: string;
  name: string;
  channelType: string;
  channelId: string;
}

interface StreamConversationParams {
  endpoint: string;
  secondaryOrgId: string;
  sessionId: string;
  /** When provided, tries refresh first to restore existing session; falls back to create if refresh fails */
  existingSessionId?: string;
}

function toResult(data: StreamConversationData['data']): StreamConversationResult {
  return {
    user: data.session_id,
    token: data.user_token,
    name: 'Anonymous User',
    channelType: 'messaging',
    channelId: data.conversation_id,
  };
}

export async function getStreamConversation({
  endpoint,
  secondaryOrgId,
  sessionId,
  existingSessionId,
}: StreamConversationParams): Promise<StreamConversationResult> {
  if (existingSessionId) {
    const refreshUrl = new URL(
      `/api/v4/cms/public/chat/sessions/${existingSessionId}/refresh`,
      endpoint,
    ).toString();
    const params = {
      session_id: existingSessionId,
      secondary_organization_id: Number(secondaryOrgId),
    };
    const response = await axios.post<StreamConversationData>(
      refreshUrl, params,
      { validateStatus: (status) => status < 500 },
    );
    if (response.status === 200 && response.data?.success) {
      return toResult(response.data.data);
    }
  }

  const createUrl = new URL('/api/v4/cms/public/chat/sessions', endpoint).toString();
  const { data } = await axios.post<StreamConversationData>(createUrl, {
    session_id: sessionId,
    secondary_organization_id: Number(secondaryOrgId),
  });
  return toResult(data.data);
}

export async function deleteSession({
  endpoint,
  secondaryOrgId,
  sessionId,
}: StreamConversationParams): Promise<void> {
  try {
    const chatSessionUrl = new URL('/api/v4/cms/public/chat/sessions', endpoint).toString();
    const params = {
      session_id: sessionId,
      secondary_organization_id: Number(secondaryOrgId),
    }
    // use fetch with keepalive to ensure the request completes even after page unload
    // axios does not support keepalive
    await fetch(chatSessionUrl, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      keepalive: true,
    });
  } catch (e) {
  // Silently fail on session deletion to avoid disrupting user experience
    console.warn('Failed to delete chat session:', e instanceof Error ? e.message : 'Unknown error');
  }
}
